// ✅ تعديل الكود ليعمل بنفس أسلوب DriverTrackingReports (فلتر واحد على timestamp)

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import AdminSidebar from '../components/AdminSidebar';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './DriverTrackingHistory.css';
import carGreen from '../assets/1.png';
import carRed from '../assets/3.png';

const carIcons = {
  moving: new L.Icon({ iconUrl: carGreen, iconSize: [40, 40], iconAnchor: [20, 40], popupAnchor: [0, -40] }),
  stopped: new L.Icon({ iconUrl: carRed, iconSize: [40, 40], iconAnchor: [20, 40], popupAnchor: [0, -40] })
};

const DriverTrackingHistory = () => {
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [movementsData, setMovementsData] = useState([]);
  const [pathPolyline, setPathPolyline] = useState([]);
  const [mapCenter, setMapCenter] = useState([29.3759, 47.9774]);
  const [mapZoom, setMapZoom] = useState(12);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalDistance: 0,
    averageSpeed: 0,
    maxSpeed: 0,
    movingTime: 0,
    stoppedTime: 0,
    stopsCount: 0
  });

  const navigate = useNavigate();

  const formatTime = useCallback((date) => date.toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit', second: '2-digit' }), []);
  const calculateDistance = useCallback((lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180) * Math.cos(lat2*Math.PI/180) * Math.sin(dLon/2)**2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  }, []);

  const isStopPoint = useCallback((m) => m.isMoving === false || m.speed < 3, []);

  const fetchDrivers = useCallback(async () => {
    const driversRef = collection(db, 'drivers');
    const snapshot = await getDocs(driversRef);
    setDrivers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
  }, []);

  const fetchMovements = useCallback(async () => {
    if (!selectedDriver || !dateFrom || !dateTo) return;
    setLoading(true);
    try {
      const start = new Date(dateFrom + 'T00:00:00');
      const end = new Date(dateTo + 'T23:59:59');

      const q = query(
        collection(db, 'driverMovements'),
        where('timestamp', '>=', start),
        orderBy('timestamp', 'asc')
      );

      const snapshot = await getDocs(q);
      const allMovements = snapshot.docs.map(doc => {
        const d = doc.data();
        const timestamp = d.timestamp?.toDate() || new Date();
        return {
          id: doc.id,
          location: d.location || { lat: 0, lng: 0 },
          timestamp,
          formattedTime: formatTime(timestamp),
          speed: d.speed || 0,
          isMoving: d.isMoving !== undefined ? d.isMoving : (d.speed > 3),
          driverId: d.driverId,
          driverName: d.driverName || ''
        };
      });

      const movements = allMovements.filter(m =>
        m.driverId === selectedDriver &&
        m.timestamp >= start &&
        m.timestamp <= end
      );

      setMovementsData(movements);

      if (movements.length) {
        const polyline = movements.map(m => [m.location.lat, m.location.lng]);
        setPathPolyline(polyline);
        setMapCenter(polyline[0]);
        setMapZoom(14);

        let dist = 0, speedSum = 0, maxSpeed = 0, moveCount = 0, stopCount = 0, stops = 0, stopped = false;
        for (let i = 1; i < movements.length; i++) {
          const prev = movements[i - 1];
          const curr = movements[i];
          dist += calculateDistance(prev.location.lat, prev.location.lng, curr.location.lat, curr.location.lng);
          speedSum += curr.speed;
          maxSpeed = Math.max(maxSpeed, curr.speed);
          if (isStopPoint(curr)) {
            stops++;
            if (!stopped) { stopCount++; stopped = true; }
          } else { moveCount++; stopped = false; }
        }

        setStats({
          totalDistance: dist.toFixed(2),
          averageSpeed: (speedSum / movements.length).toFixed(1),
          maxSpeed: maxSpeed.toFixed(1),
          movingTime: moveCount,
          stoppedTime: stops,
          stopsCount: stopCount
        });
      } else {
        setStats({ totalDistance: 0, averageSpeed: 0, maxSpeed: 0, movingTime: 0, stoppedTime: 0, stopsCount: 0 });
        setPathPolyline([]);
        setMapCenter([29.3759, 47.9774]);
        setMapZoom(12);
      }
    } catch (error) {
      console.error('Error fetching movements:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedDriver, dateFrom, dateTo, calculateDistance, isStopPoint, formatTime]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) return navigate('/login');
      const role = localStorage.getItem('userRole');
      if (role !== 'admin') return navigate('/');
      await fetchDrivers();
      setLoading(false);
    });
    return () => unsub();
  }, [navigate, fetchDrivers]);

  useEffect(() => {
    fetchMovements();
  }, [fetchMovements]);

  return (
    <div className="driver-tracking-history-layout">
      <AdminSidebar />
      <div className="driver-tracking-history-content">
        <div className="tracking-history-header">
          <h1>سجل تحركات السائقين</h1>
          <div className="tracking-controls">
            <div className="control-group">
              <label>اختر السائق:</label>
              <select value={selectedDriver} onChange={(e) => setSelectedDriver(e.target.value)}>
                <option value="">-- اختر السائق --</option>
                {drivers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div className="control-group">
              <label>من تاريخ:</label>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </div>
            <div className="control-group">
              <label>إلى تاريخ:</label>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </div>
          </div>
        </div>
        
        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>جاري تحميل البيانات...</p>
          </div>
        ) : (
          <>
            {!selectedDriver ? (
              <div className="no-selection-message">
                <p>الرجاء اختيار سائق لعرض سجل تحركاته</p>
              </div>
            ) : movementsData.length === 0 ? (
              <div className="no-data-message">
                <p>لا توجد بيانات تحركات متاحة لهذا السائق في التاريخ المحدد</p>
              </div>
            ) : (
              <>
                {/* بطاقات الإحصائيات */}
                <div className="stats-cards">
                  <div className="stat-card">
                    <div className="stat-value">{stats.totalDistance} كم</div>
                    <div className="stat-label">المسافة الإجمالية</div>
                  </div>
                  
                  <div className="stat-card">
                    <div className="stat-value">{stats.averageSpeed} كم/س</div>
                    <div className="stat-label">متوسط السرعة</div>
                  </div>
                  
                  <div className="stat-card">
                    <div className="stat-value">{stats.maxSpeed} كم/س</div>
                    <div className="stat-label">أقصى سرعة</div>
                  </div>
                  
                  <div className="stat-card">
                    <div className="stat-value">{stats.stopsCount}</div>
                    <div className="stat-label">عدد التوقفات</div>
                  </div>
                </div>
                
                {/* قسم الخريطة */}
                <div className="map-container">
                  <div className="map-legend">
                    <div className="legend-item">
                      <div className="legend-color moving"></div>
                      <span>نقاط الحركة</span>
                    </div>
                    <div className="legend-item">
                      <div className="legend-color stopped"></div>
                      <span>نقاط التوقف</span>
                    </div>
                    <div className="legend-item">
                      <div className="legend-color route"></div>
                      <span>المسار</span>
                    </div>
                  </div>
                  
                  <MapContainer center={mapCenter} zoom={mapZoom} className="leaflet-map">
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    
                    {/* خط المسار */}
                    {pathPolyline.length > 0 && (
                      <Polyline
                        positions={pathPolyline}
                        color="#2979ff"
                        weight={4}
                        opacity={0.7}
                      />
                    )}
                    
                    {/* العلامات (الماركرات) */}
                    {movementsData.map((movement) => (
                      <Marker
                        key={movement.id}
                        position={[movement.location.lat, movement.location.lng]}
                        icon={isStopPoint(movement) ? carIcons.stopped : carIcons.moving}
                      >
                        <Popup>
                          <div className="movement-popup">
                            <h3>{movement.driverName || "سائق"}</h3>
                            <div className="popup-row">
                              <span className="popup-label">الوقت:</span>
                              <span className="popup-value">{movement.formattedTime}</span>
                            </div>
                            <div className="popup-row">
                              <span className="popup-label">السرعة:</span>
                              <span className="popup-value">{movement.speed.toFixed(1)} كم/س</span>
                            </div>
                            <div className="popup-row">
                              <span className="popup-label">الحالة:</span>
                              <span className="popup-value">
                                {isStopPoint(movement) ? 'متوقف' : 'متحرك'}
                              </span>
                            </div>
                            <div className="popup-row">
                              <span className="popup-label">الموقع:</span>
                              <span className="popup-value">
                                {movement.location.lat.toFixed(6)}, {movement.location.lng.toFixed(6)}
                              </span>
                            </div>
                          </div>
                        </Popup>
                      </Marker>
                    ))}
                  </MapContainer>
                </div>
                
                {/* جدول البيانات التفصيلية */}
                <div className="movements-table-section">
                  <h2>تفاصيل التحركات</h2>
                  <div className="table-container">
                    <table className="movements-table">
                      <thead>
                        <tr>
                          <th>الوقت</th>
                          <th>السرعة (كم/س)</th>
                          <th>الحالة</th>
                          <th>الموقع (خط العرض/الطول)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {movementsData.map((movement) => (
                          <tr 
                            key={movement.id}
                            className={isStopPoint(movement) ? 'stopped-row' : 'moving-row'}
                          >
                            <td>{movement.formattedTime}</td>
                            <td>{movement.speed.toFixed(1)}</td>
                            <td>
                              <span className={`status-badge ${isStopPoint(movement) ? 'stopped' : 'moving'}`}>
                                {isStopPoint(movement) ? 'متوقف' : 'متحرك'}
                              </span>
                            </td>
                            <td>
                              {movement.location.lat.toFixed(6)}, {movement.location.lng.toFixed(6)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DriverTrackingHistory; 